"use client";

import { useEffect, useRef, type RefObject } from "react";

// How often to check staleness while the page stays visible. The check itself is
// free (no network) — it only calls refetch once the data is past staleAfterMs.
const CHECK_INTERVAL_MS = 60_000;

/**
 * Calls `refetch` when data fetched at `lastFetchedAtRef.current` is older than
 * `staleAfterMs` and the user is (back) on the page: on tab refocus, window focus,
 * bfcache restore (pageshow), or periodically while the page stays visible —
 * a tab left in the foreground never fires visibilitychange.
 */
export function useRefetchWhenStale(
  refetch: () => void,
  lastFetchedAtRef: RefObject<number>,
  staleAfterMs: number,
) {
  const refetchRef = useRef(refetch);
  useEffect(() => {
    refetchRef.current = refetch;
  }, [refetch]);

  useEffect(() => {
    // Focus + visibilitychange (+ the interval) often fire together; don't fire
    // a second refetch while the first is still in flight or just failed.
    let lastTriggeredAt = 0;

    const check = () => {
      if (document.visibilityState !== "visible") return;
      // 0 = never fetched successfully — the caller's initial fetch owns that case.
      if (!lastFetchedAtRef.current) return;
      const now = Date.now();
      if (now - lastFetchedAtRef.current < staleAfterMs) return;
      if (now - lastTriggeredAt < CHECK_INTERVAL_MS) return;
      lastTriggeredAt = now;
      refetchRef.current();
    };

    const intervalId = window.setInterval(check, CHECK_INTERVAL_MS);
    document.addEventListener("visibilitychange", check);
    window.addEventListener("focus", check);
    window.addEventListener("pageshow", check);

    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener("visibilitychange", check);
      window.removeEventListener("focus", check);
      window.removeEventListener("pageshow", check);
    };
  }, [lastFetchedAtRef, staleAfterMs]);
}
